/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace lognormal
*/
var lognormal = {};

/**
* @name cdf
* @memberof lognormal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/lognormal/cdf}
*/
import cdf from './../../../../base/dists/lognormal/cdf';
setReadOnly( lognormal, 'cdf', cdf );

/**
* @name LogNormal
* @memberof lognormal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/lognormal/ctor}
*/
import LogNormal from './../../../../base/dists/lognormal/ctor';
setReadOnly( lognormal, 'LogNormal', LogNormal );

/**
* @name entropy
* @memberof lognormal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/lognormal/entropy}
*/
import entropy from './../../../../base/dists/lognormal/entropy';
setReadOnly( lognormal, 'entropy', entropy );

/**
* @name kurtosis
* @memberof lognormal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/lognormal/kurtosis}
*/
import kurtosis from './../../../../base/dists/lognormal/kurtosis';
setReadOnly( lognormal, 'kurtosis', kurtosis );

/**
* @name logcdf
* @memberof lognormal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/lognormal/logcdf}
*/
import logcdf from './../../../../base/dists/lognormal/logcdf';
setReadOnly( lognormal, 'logcdf', logcdf );

/**
* @name logpdf
* @memberof lognormal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/lognormal/logpdf}
*/
import logpdf from './../../../../base/dists/lognormal/logpdf';
setReadOnly( lognormal, 'logpdf', logpdf );

/**
* @name mean
* @memberof lognormal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/lognormal/mean}
*/
import mean from './../../../../base/dists/lognormal/mean';
setReadOnly( lognormal, 'mean', mean );

/**
* @name median
* @memberof lognormal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/lognormal/median}
*/
import median from './../../../../base/dists/lognormal/median';
setReadOnly( lognormal, 'median', median );

/**
* @name mode
* @memberof lognormal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/lognormal/mode}
*/
import mode from './../../../../base/dists/lognormal/mode';
setReadOnly( lognormal, 'mode', mode );

/**
* @name pdf
* @memberof lognormal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/lognormal/pdf}
*/
import pdf from './../../../../base/dists/lognormal/pdf';
setReadOnly( lognormal, 'pdf', pdf );

/**
* @name quantile
* @memberof lognormal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/lognormal/quantile}
*/
import quantile from './../../../../base/dists/lognormal/quantile';
setReadOnly( lognormal, 'quantile', quantile );

/**
* @name skewness
* @memberof lognormal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/lognormal/skewness}
*/
import skewness from './../../../../base/dists/lognormal/skewness';
setReadOnly( lognormal, 'skewness', skewness );

/**
* @name stdev
* @memberof lognormal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/lognormal/stdev}
*/
import stdev from './../../../../base/dists/lognormal/stdev';
setReadOnly( lognormal, 'stdev', stdev );

/**
* @name variance
* @memberof lognormal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/lognormal/variance}
*/
import variance from './../../../../base/dists/lognormal/variance';
setReadOnly( lognormal, 'variance', variance );


// EXPORTS //

export default lognormal;
